import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "../lib/api";
import { fmtCurrency, fmtNum, fmtPct, marketPill, scoreColor, scoreChipClass, kindLabel } from "../lib/format";
import PriceChart from "../components/PriceChart";
import NewsPanel from "../components/NewsPanel";

export default function TransactionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get(`/transactions/${id}`).then((r) => setData(r.data));
  }, [id]);

  if (!data) return <div className="p-6 mono terminal-loader">Loading</div>;
  const tx = data.transaction;
  const bd = tx.score_breakdown || {};

  return (
    <div data-testid="tx-detail-page">
      <button onClick={() => nav(-1)} className="text-[12px] text-[color:var(--text-secondary)] hover:text-[color:var(--cyan)] mb-5" data-testid="back-btn">← Back</button>

      <div className="glass p-6 mb-6 flex items-center gap-5">
        <span className={marketPill(tx.market)}>{tx.market}</span>
        <h1 className="mono text-2xl font-semibold text-[color:var(--cyan)] tracking-tight">{tx.ticker}</h1>
        <div className="text-[color:var(--text-secondary)]">{tx.company}</div>
        <div className="ml-auto flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-medium">Conviction score</div>
            <div className="mono text-[36px] font-semibold leading-none grad-text mt-1" data-testid="tx-score">{(tx.score * 100).toFixed(0)}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_1fr] gap-4 mb-6">
        <div className="glass p-6">
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-medium mb-4">Extracted Data</div>
          <Row k="Insider" v={tx.insider_name} />
          <Row k="Role" v={tx.insider_role} />
          <Row k="Kind" v={<span style={{ color: tx.kind.includes("buy") ? "var(--score-high)" : tx.kind.includes("sell") ? "var(--pink)" : "var(--text-secondary)" }}>{kindLabel(tx.kind)}</span>} />
          <Row k="Shares" v={<span className="mono">{fmtNum(tx.shares)}</span>} />
          <Row k="Price" v={<span className="mono">{fmtCurrency(tx.price, tx.currency)}</span>} />
          <Row k="Value" v={<span className="mono font-semibold">{fmtCurrency(tx.value, tx.currency)}</span>} />
          <Row k="Transaction Date" v={<span className="mono">{tx.transaction_date}</span>} />
          <Row k="Filing Date" v={<span className="mono">{tx.filing_date}</span>} />
          <Row k="Market Cap" v={<span className="mono">{fmtCurrency(tx.market_cap, tx.currency)}</span>} />
          <Row k="5D Price Change" v={<span className="mono" style={{ color: tx.price_change_5d_pct >= 0 ? "var(--score-high)" : "var(--pink)" }}>{fmtPct(tx.price_change_5d_pct)}</span>} />
          <Row k="Extraction Method" v={<span className="mono text-[color:var(--cyan)]">{tx.extraction_method}</span>} />
          <Row k="Confidence" v={<span className="mono">{(tx.confidence * 100).toFixed(1)}%</span>} />
        </div>

        <div className="glass p-6">
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-medium mb-4">Source Document</div>
          <div className="mono text-xs rounded-xl bg-black/25 border border-white/[0.06] p-4 whitespace-pre-wrap text-[color:var(--text-secondary)] min-h-[240px] leading-relaxed" data-testid="source-excerpt">
            {tx.source_doc_excerpt}
          </div>
          <a href={tx.source_url} target="_blank" rel="noreferrer" className="inline-flex mt-3 text-[12px] mono text-[color:var(--cyan)] hover:brightness-125">
            → Open original filing
          </a>
        </div>
      </div>

      {/* share price chart */}
      <div className="mb-6">
        <PriceChart ticker={tx.ticker} filingDate={tx.filing_date} kind={tx.kind} currency={tx.currency} />
      </div>

      {/* score breakdown */}
      <div className="glass mb-6 overflow-hidden" data-testid="score-breakdown">
        <div className="data-header flex items-center justify-between">
          <span>How was this calculated? · v{tx.score_version}</span>
          <span className="mono text-[color:var(--cyan)]">TOTAL {(tx.score * 100).toFixed(1)}</span>
        </div>
        <div className="grid grid-cols-5">
          {Object.entries(bd.components || {}).map(([k, v], i, arr) => (
            <div key={k} className={`p-5 ${i < arr.length - 1 ? "border-r border-white/[0.04]" : ""}`}>
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-medium">{k.replace(/_/g, " ")}</div>
              <div className="mono text-2xl font-semibold mt-2 grad-text">{(v * 100).toFixed(1)}</div>
              <div className="h-1 bg-white/[0.05] mt-3 rounded-full overflow-hidden">
                <div style={{ width: `${Math.min(v * 200, 100)}%`, background: "linear-gradient(90deg, var(--violet), var(--cyan))", height: "100%" }} />
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-white/[0.05] p-4 mono text-xs text-[color:var(--text-secondary)]">
          RAW · size_ratio={bd.raw?.size_ratio} · value/mcap_log={bd.raw?.value_over_mcap_log} · role_weight={bd.raw?.role_weight} · cluster={bd.raw?.cluster_count} · 5d_change={bd.raw?.price_change_5d_pct}%
        </div>
      </div>

      {/* recent news */}
      <div className="mb-6">
        <NewsPanel ticker={tx.ticker} />
      </div>

      {/* score history */}
      {tx.score_history && tx.score_history.length > 0 && (
        <div className="glass mb-6 overflow-hidden" data-testid="score-history">
          <div className="data-header">Score History · {tx.score_history.length} prior version{tx.score_history.length > 1 ? "s" : ""}</div>
          <div className="grid grid-cols-[1fr_140px_140px_140px_100px] data-header text-[color:var(--text-tertiary)] gap-2">
            <div>VERSION</div>
            <div className="text-right">SCORE</div>
            <div>STATUS</div>
            <div>AT</div>
            <div className="text-right">Δ VS CURRENT</div>
          </div>
          {[...tx.score_history].reverse().map((h) => (
            <div key={`${h.version}-${h.at}`} className="grid grid-cols-[1fr_140px_140px_140px_100px] border-t border-white/[0.04] gap-2">
              <div className="data-cell mono text-xs text-[color:var(--text-secondary)]">{h.version}</div>
              <div className="data-cell mono text-right" style={{ color: scoreColor(h.score) }}>{(h.score * 100).toFixed(1)}</div>
              <div className="data-cell mono text-xs text-[color:var(--text-secondary)]">{h.status}</div>
              <div className="data-cell mono text-[11px] text-[color:var(--text-tertiary)]">{(h.at || "").slice(0, 16).replace("T", " ")}</div>
              <div className="data-cell mono text-right font-semibold" style={{ color: (tx.score - h.score) >= 0 ? "var(--score-high)" : "var(--pink)" }}>
                {(tx.score - h.score) >= 0 ? "+" : ""}{((tx.score - h.score) * 100).toFixed(1)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* history */}
      <div className="glass overflow-hidden" data-testid="insider-history">
        <div className="data-header">Insider History · {tx.insider_name} · {data.insider_history.length} prior filings</div>
        {data.insider_history.length === 0 && <div className="data-cell mono text-[color:var(--text-tertiary)]">No other filings on record.</div>}
        {data.insider_history.map((h) => (
          <div key={h.id} onClick={() => nav(`/tx/${h.id}`)} className="grid grid-cols-[80px_100px_1fr_130px_140px_110px_90px] border-t border-white/[0.04] hover:bg-white/[0.03] cursor-pointer items-center">
            <div className="data-cell"><span className={marketPill(h.market)}>{h.market}</span></div>
            <div className="data-cell mono font-semibold text-[color:var(--cyan)]">{h.ticker}</div>
            <div className="data-cell text-sm truncate text-[color:var(--text-secondary)]">{h.company}</div>
            <div className="data-cell mono text-right">{fmtNum(h.shares)}</div>
            <div className="data-cell mono text-right">{fmtCurrency(h.value, h.currency)}</div>
            <div className="data-cell mono text-xs text-[color:var(--text-tertiary)]">{h.filing_date}</div>
            <div className="data-cell text-right"><span className={scoreChipClass(h.score)}>{(h.score * 100).toFixed(0)}</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center py-2.5 border-b border-white/[0.04] last:border-b-0">
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] w-40 font-medium">{k}</div>
      <div className="text-sm">{v}</div>
    </div>
  );
}
